"use client";

import { Card } from "@/component/override/card.c";
import { routes } from "@/config/routes";
import { useLanguage } from "@/context/language-context";
import { useTranslations } from "@/hooks/use-translations";
import { Config } from "@/shared/config";
import {
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider
} from "@heroui/react";
import { IconArrowRight, IconCheck, IconShieldCheck, IconX } from "@tabler/icons-react";
import { DateTime } from "luxon";
import Link from "next/link";
import { useMemo, type ReactNode } from "react";
import { CtaButton } from "../../../component/cta-button";

interface FeatureItemProps {
  text: string;
  subtitle?: ReactNode;
  isIncluded: boolean;
  isPremium?: boolean;
}

interface PricingProps {
  showTitle?: boolean;
  hideCallToAction?: boolean;
}

export default function Pricing({ showTitle = true, hideCallToAction = false }: PricingProps) {
  const t = useTranslations();
  const { language } = useLanguage();

  const discountValidUntil = useMemo(() => (
    DateTime.now()
      .setZone("Europe/Rome")
      .setLocale(language)
      .endOf("month")
      .toLocaleString(DateTime.DATE_FULL)
  ), [language]);

  if (!t) return null;

  return (
    <section id="pricing" className="flex flex-col items-center gap-4">
      {showTitle && <h2 className="text-5xl font-bold mb-4">{t.pricing.title}</h2>}

      <div className="w-full max-w-sm lg:max-w-6xl">
        <Card className="w-full">
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <CardHeader className="flex flex-col items-center gap-2 p-6">
              <h3 className="text-xl">{t.pricing.plan.name}</h3>
              <div className="flex flex-col items-center gap-1">
                <div className="flex items-center gap-3">
                  <p className="text-5xl font-bold">{Config.MEMBERSHIP_DISCOUNT_MONTHLY_PRICE}€</p>
                  <p className="text-2xl font-bold text-default-400 line-through">{Config.MEMBERSHIP_FULL_MONTHLY_PRICE}€</p>
                </div>
                <p className="text-sm text-default-500">{t.pricing.plan.billing} {t.pricing.vatSuffix.replace("{{vat}}", `${Config.MEMBERSHIP_VAT}`)}</p>
              </div>

              {/* Referral Discount Info */}
              <div className="mt-3">
                <Card
                  className="border border-primary-200 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/60 px-10 py-4 transition-all duration-200"
                >
                  <CardBody className="p-6 text-center">
                    <h4 className="font-semibold text-primary-800 dark:text-primary-800 mb-2">
                      {t.pricing.launchOffer.title}
                    </h4>
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {t.pricing.launchOffer.validUntil.replace("{{date}}", discountValidUntil)}
                    </p>
                    <p className="text-xs text-default-500 mt-2">
                      {t.pricing.launchOffer.subsequentPrice
                        .replace("{{price}}", Config.MEMBERSHIP_FULL_MONTHLY_PRICE)
                        .replace("{{vat}}", Config.MEMBERSHIP_VAT)}
                    </p>
                  </CardBody>
                </Card>
              </div>

              <p className="mt-4 text-sm text-default-600 text-center">
                {t.pricing.carnetOption}
              </p>
            </CardHeader>
            <Divider />
            <CardBody className="gap-6 px-4 pt-6">
              <div className="flex flex-col gap-4">
                {t.pricing.plan.features.map((feature: string, index: number) => {
                  switch (index) {
                    case 0:
                      return (
                        <div key={index}>
                          <FeatureItem
                            text={feature}
                            isIncluded={true}
                          />
                          <CtaButton
                            as={Link}
                            href={routes.howReservationsWork(language)}
                            size="sm"
                            className="ml-6 text-xs h-6 px-2"
                            endContent={<IconArrowRight size={12} />}>
                            {t.pricing.plan.howItWorks}
                          </CtaButton>
                        </div>
                      );
                    default:
                      return (
                        <FeatureItem
                          key={index}
                          text={feature}
                          isIncluded={true}
                        />
                      );
                  }
                })}
              </div>

              {/* Verification Notice - Mobile */}
              <div className="mt-6 pt-6 border-t border-divider">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Chip
                    color="warning"
                    variant="flat"
                    startContent={<IconShieldCheck size={14} />}
                  >
                    {t.pricing.verification.badge}
                  </Chip>
                </div>
                <div className="px-4 py-3 bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-700 rounded-lg">
                  <p className="text-sm text-warning-800 dark:text-warning-200 text-center">
                    <strong>{t.pricing.verification.title}</strong>
                  </p>
                  <p className="text-xs text-warning-700 dark:text-warning-300 text-center mt-1">
                    {t.pricing.verification.description}
                  </p>
                  <p className="text-xs text-warning-600 dark:text-warning-400 text-center mt-2 font-medium">
                    {t.pricing.verification.process}
                  </p>
                </div>
              </div>
            </CardBody>
            {!hideCallToAction && (
              <CardFooter className="flex flex-col gap-4 justify-center mb-4">
                <p className="text-sm text-default-500">
                  {t.pricing.plan.description}
                </p>

                <CtaButton
                  as={Link}
                  href={routes.preRegistration(language)}
                  className="plausible-event-name=cta-pricing"
                  endContent={<IconArrowRight size={20} />}>
                  {t.pricing.plan.cta}
                </CtaButton>
              </CardFooter>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex lg:flex-row">
            {/* Left Side - Price */}
            <div className="flex-1 p-8 flex flex-col justify-center items-center border-r border-divider">
              <h3 className="text-2xl font-semibold mb-6">{t.pricing.plan.name}</h3>
              <div className="flex flex-col items-center gap-3 mb-4">
                <div className="flex items-center gap-4">
                  <p className="text-6xl font-bold">{Config.MEMBERSHIP_DISCOUNT_MONTHLY_PRICE}€</p>
                  <p className="text-3xl font-bold text-default-400 line-through">{Config.MEMBERSHIP_FULL_MONTHLY_PRICE}€</p>
                </div>
                <p className="text-base text-default-500">{t.pricing.plan.billing} {t.pricing.vatSuffix.replace("{{vat}}", `${Config.MEMBERSHIP_VAT}`)}</p>
              </div>

              {/* Referral Discount Info */}
              <div className="mb-6 w-full max-w-md">
                <Card
                  className="border border-primary-200 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/60 transition-all duration-200"
                >
                  <CardBody className="p-6 text-center">
                    <h4 className="font-semibold text-primary-800 dark:text-primary-800 mb-2">
                      {t.pricing.launchOffer.title}
                    </h4>
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {t.pricing.launchOffer.validUntil.replace("{{date}}", discountValidUntil)}
                    </p>
                    <p className="text-xs text-default-500 mt-2">
                      {t.pricing.launchOffer.subsequentPrice
                        .replace("{{price}}", Config.MEMBERSHIP_FULL_MONTHLY_PRICE)
                        .replace("{{vat}}", Config.MEMBERSHIP_VAT)}
                    </p>
                  </CardBody>
                </Card>
              </div>

              <p className="mb-6 text-sm text-default-600 text-center">
                {t.pricing.carnetOption}
              </p>

              {!hideCallToAction && (
                <div className="flex flex-col gap-4 items-center">
                  <p className="text-sm text-default-500 text-center">
                    {t.pricing.plan.description}
                  </p>
                  <CtaButton
                    as={Link}
                    href={routes.preRegistration(language)}
                    className="plausible-event-name=cta-pricing"
                    endContent={<IconArrowRight size={20} />}>
                    {t.pricing.plan.cta}
                  </CtaButton>
                </div>
              )}
            </div>

            {/* Right Side - Features */}
            <div className="flex-1 p-8 flex flex-col justify-center">
              <h4 className="text-xl font-semibold mb-6 text-center">{t.pricing.plan.featuresTitle}</h4>
              <div className="flex flex-col gap-3">
                {t.pricing.plan.features.map((feature: string, index: number) => {
                  switch (index) {
                    case 0:
                      return (
                        <div key={index}>
                          <FeatureItem
                            text={feature}
                            isIncluded={true}
                          />
                          <CtaButton
                            as={Link}
                            href={routes.howReservationsWork(language)}
                            size="sm"
                            className="ml-6 text-xs h-6 px-2"
                            endContent={<IconArrowRight size={12} />}>
                            {t.pricing.plan.howItWorks}
                          </CtaButton>
                        </div>
                      );
                    default:
                      return (
                        <FeatureItem
                          key={index}
                          text={feature}
                          isIncluded={true}
                        />
                      );
                  }
                })}
              </div>

              {/* Verification Notice - Desktop */}
              <div className="mt-8 pt-6 border-t border-divider">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Chip
                    color="warning"
                    variant="flat"
                    startContent={<IconShieldCheck size={14} />}
                  >
                    {t.pricing.verification.badge}
                  </Chip>
                </div>
                <div className="px-4 py-3 bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-700 rounded-lg">
                  <p className="text-sm text-warning-800 dark:text-warning-200 text-center">
                    <strong>{t.pricing.verification.title}</strong>
                  </p>
                  <p className="text-xs text-warning-700 dark:text-warning-300 text-center mt-1">
                    {t.pricing.verification.description}
                  </p>
                  <p className="text-xs text-warning-600 dark:text-warning-400 text-center mt-2 font-medium">
                    {t.pricing.verification.process}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

    </section>
  );
}

function FeatureItem({
  text,
  subtitle,
  isIncluded,
  isPremium = false,
}: FeatureItemProps) {
  return (
    <div className="flex items-center gap-2">
      {isIncluded ? (
        <IconCheck
          className={isPremium ? "text-foreground" : "text-success"}
          width={20}
        />
      ) : (
        <IconX className="text-default-400" width={20} />
      )}
      <div className="flex flex-col">
        <span className={!isIncluded ? "text-default-400" : "text-sm"}>{text}</span>
        {subtitle}
      </div>
    </div>
  );
}
